import React, { useEffect, useRef } from "react";
import Pusher from "pusher-js";
import html2canvas from "html2canvas";
import api from "../../services/api";

const PUSHER_KEY = "540d521fe5fe005cb99d";
const PUSHER_CLUSTER = "ap2";

const ScreenCaptureListener = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const token = localStorage.getItem("token");
  const isCapturing = useRef(false);

  useEffect(() => {
    if (!user.id || user.role === 'admin') return;

    const pusher = new Pusher(PUSHER_KEY, { cluster: PUSHER_CLUSTER });
    const channel = pusher.subscribe(`employee-${user.id}`);

    channel.bind("screen-capture-request", async (data) => {
      if (isCapturing.current) return;
      isCapturing.current = true;

      try {
        if (data.capture_type === 'screenshot') {
          await captureBestAvailable(data.capture_id);
        } else if (data.capture_type === 'video') {
          await captureBestAvailable(data.capture_id, data.duration || 10);
        }
      } catch (error) {
        console.error("Capture failed:", error);
      } finally {
        isCapturing.current = false;
      }
    });

    return () => {
      channel.unbind("screen-capture-request");
      pusher.unsubscribe(`employee-${user.id}`);
      pusher.disconnect();
    };
  }, [user.id, token]);

  // Try multiple capture methods, use whichever works
  const captureBestAvailable = async (captureId, duration = null) => {
    // Method 1: Try to capture current visible tab content
    try {
      if (document.visibilityState === 'visible') {
        // Tab is active - use html2canvas (best quality)
        const canvas = await html2canvas(document.body, {
          useCORS: true,
          allowTaint: true,
          scale: 0.6,
          logging: false,
        });
        const blob = await new Promise(r => canvas.toBlob(r, "image/jpeg", 0.75));
        await uploadCapture(blob, captureId, 'screenshot', 'image/jpeg');
        return;
      }
    } catch (e) {
      console.log("html2canvas failed, trying alternatives...");
    }

    // Method 2: Try getDisplayMedia (shows browser picker - works for any screen)
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { mediaSource: 'screen' },
        audio: false
      });

      if (duration) {
        // Video recording
        const chunks = [];
        const recorder = new MediaRecorder(stream, {
          mimeType: MediaRecorder.isTypeSupported('video/webm; codecs=vp9') 
            ? 'video/webm; codecs=vp9' : 'video/webm'
        });
        
        recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
        
        recorder.onstop = async () => {
          stream.getTracks().forEach(t => t.stop());
          const blob = new Blob(chunks, { type: 'video/webm' });
          await uploadCapture(blob, captureId, 'video', 'video/webm');
        };
        
        recorder.start(1000);
        setTimeout(() => { if (recorder.state === 'recording') recorder.stop(); }, duration * 1000);
      } else {
        // Screenshot from stream
        const video = document.createElement('video');
        video.srcObject = stream;
        await video.play();
        
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        
        stream.getTracks().forEach(t => t.stop());
        
        const blob = await new Promise(r => canvas.toBlob(r, "image/jpeg", 0.75));
        await uploadCapture(blob, captureId, 'screenshot', 'image/jpeg');
      }
      return;
    } catch (e) {
      console.log("getDisplayMedia failed or cancelled");
    }

    // Method 3: Last resort - capture just the page title and URL
    const fallbackData = {
      title: document.title,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      visibilityState: document.visibilityState
    };
    
    const fallbackBlob = new Blob([JSON.stringify(fallbackData)], { type: 'application/json' });
    await uploadCapture(fallbackBlob, captureId, 'screenshot', 'application/json');
  };

  const uploadCapture = async (blob, captureId, captureType, mimeType) => {
    const formData = new FormData();
    const extension = mimeType.includes('video') ? 'webm' : 
                      mimeType.includes('json') ? 'json' : 'jpg';
    const fileName = `capture_${Date.now()}.${extension}`;
    
    formData.append(captureType === 'video' ? 'video' : 'screenshot', blob, fileName);
    formData.append('capture_id', captureId);
    formData.append('capture_type', captureType);

    await api.post("/user/upload_screen_capture", formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });
  };

  return null;
};

export default ScreenCaptureListener;